import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import CampaignLeadsList from './CampaignLeadsList';
import EntityDetailPanel from './EntityDetailPanel';
import TableView from './TableView';
import CampaignLaunch from './CampaignLaunch';
import CampaignPerformance from './CampaignPerformance';
import CampaignOutreach from './CampaignOutreach';

// Campaign workspace — the dual-mode container detail view, laid out as the
// four-tab shell from the design doc (Sequence / Leads List / Launch /
// Performance). Phase A: Leads List renders the pipeline board (parity with
// the old Jobs / BD Campaigns screens) on the unified /api/campaigns API.

type TabKey = 'todo' | 'sequence' | 'leads' | 'launch' | 'performance';

const MODE_LABEL: Record<string, string> = { delivery: 'Delivery', bd: 'BD Outreach' };
const MODE_BADGE: Record<string, string> = {
  delivery: 'bg-blue-100 text-blue-700',
  bd: 'bg-purple-100 text-purple-700',
};

const CampaignWorkspace: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();

  const [campaign, setCampaign] = useState<any | null>(null);
  const [columns, setColumns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // ?tab=todo (the link Claude gives after saving outreach) opens To do.
  const [activeTab, setActiveTab] = useState<TabKey>(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return (['todo', 'sequence', 'leads', 'launch', 'performance'] as string[]).includes(t || '') ? (t as TabKey) : 'leads';
  });
  const [leadsView, setLeadsView] = useState<'table' | 'board'>('table');
  const [leadsReloadKey, setLeadsReloadKey] = useState(0);
  const [dragCardId, setDragCardId] = useState<number | null>(null);
  // Add-from-monitoring-signals modal state. A move (Person X: A → B) is one
  // event but two opportunities: a backfill (message the company they left) or a
  // job change (message the company they joined). The member is a CONTACT at the
  // target company; Person X is only the subject of the message.
  const [showSignals, setShowSignals] = useState(false);
  const [signalDirection, setSignalDirection] = useState<'backfill' | 'job_change'>('job_change');
  const [signalStart, setSignalStart] = useState('');
  const [signalEnd, setSignalEnd] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRows, setPreviewRows] = useState<any[] | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [addingSignals, setAddingSignals] = useState(false);
  const [signalMsg, setSignalMsg] = useState<string | null>(null);

  const runSignalPreview = async () => {
    setPreviewLoading(true); setSignalMsg(null); setPreviewRows(null); setSelectedContacts(new Set());
    try {
      const params = new URLSearchParams({ direction: signalDirection });
      if (signalStart) params.set('startDate', new Date(signalStart).toISOString());
      if (signalEnd) params.set('endDate', new Date(signalEnd + 'T23:59:59').toISOString());
      const res = await fetch(`/api/campaigns/${campaignId}/signal-preview?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      const rows = data.rows || [];
      setPreviewRows(rows);
      // Pre-select every existing contact by default.
      const pre = new Set<string>();
      rows.forEach((row: any, i: number) => (row.contacts || []).forEach((c: any) => pre.add(`${i}:${c.id}`)));
      setSelectedContacts(pre);
    } catch (e: any) { setSignalMsg(e.message || 'Failed to preview'); }
    finally { setPreviewLoading(false); }
  };

  const toggleSignalContact = (key: string) => setSelectedContacts(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const addSelectedSignalContacts = async () => {
    if (!previewRows) return;
    const picks: any[] = [];
    previewRows.forEach((row: any, i: number) => (row.contacts || []).forEach((c: any) => {
      if (!selectedContacts.has(`${i}:${c.id}`)) return;
      picks.push({
        contactId: c.id,
        signal: {
          subjectName: row.subjectName, subjectFirstName: row.subjectFirstName,
          subjectNewTitle: row.subjectNewTitle, subjectOldTitle: row.subjectOldTitle,
          fromCompany: row.fromCompany, toCompany: row.toCompany, targetCompany: row.targetCompany,
          moveDate: row.moveDate, changesSummary: row.changesSummary,
        },
      });
    }));
    if (picks.length === 0) { setSignalMsg('Select at least one contact to add.'); return; }
    setAddingSignals(true); setSignalMsg(null);
    try {
      const res = await fetch(`/api/campaigns/${campaignId}/add-signal-contacts`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ direction: signalDirection, picks }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setSignalMsg(`Added ${data.added} contact${data.added === 1 ? '' : 's'}${data.updated ? `, updated ${data.updated}` : ''} — see the Contacts tab in the Leads List.`);
      setLeadsReloadKey(k => k + 1);
      fetchCampaign();
    } catch (e: any) { setSignalMsg(e.message || 'Failed to add contacts'); }
    finally { setAddingSignals(false); }
  };

  const openSignals = () => {
    setShowSignals(true); setSignalMsg(null); setPreviewRows(null); setSelectedContacts(new Set());
  };
  const selectedCount = selectedContacts.size;
  const [panel, setPanel] = useState<{ open: boolean; entityType: 'candidate' | 'contact' | 'company'; entityId: number | null }>({ open: false, entityType: 'candidate', entityId: null });

  // silent = refresh in place (no full-page spinner), e.g. after To do changes a stage.
  const fetchCampaign = useCallback(async (silent: boolean = false) => {
    if (!token || !campaignId) return;
    if (silent !== true) setLoading(true);
    try {
      const resp = await fetch(`/api/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.status === 404) { setNotFound(true); return; }
      if (!resp.ok) throw new Error('Failed to fetch');
      setCampaign(await resp.json());
    } catch (err) {
      console.error('Failed to load campaign:', err);
    } finally {
      setLoading(false);
    }
  }, [token, campaignId]);

  const fetchColumns = useCallback(async () => {
    if (!token || !campaignId) return;
    try {
      const resp = await fetch(`/api/campaigns/${campaignId}/columns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) setColumns(await resp.json());
    } catch (err) {
      console.error('Failed to load campaign columns:', err);
    }
  }, [token, campaignId]);

  useEffect(() => { fetchCampaign(); fetchColumns(); }, [fetchCampaign, fetchColumns]);

  const allCards: any[] = campaign?.stages?.flatMap((s: any) => s.cards || []) || [];
  const memberCount = allCards.length;

  // ── Card / stage mutations ──────────────────────────────────────────
  const moveCard = async (cardId: number, toStageId: number) => {
    // Optimistic move to the end of the target stage.
    setCampaign((prev: any) => {
      if (!prev) return prev;
      let moved: any = null;
      const stages = prev.stages.map((s: any) => ({
        ...s,
        cards: (s.cards || []).filter((c: any) => {
          if (c.id === cardId) { moved = c; return false; }
          return true;
        }),
      }));
      if (moved) {
        const target = stages.find((s: any) => s.id === toStageId);
        if (target) target.cards = [...target.cards, { ...moved, stage_id: toStageId }];
      }
      return { ...prev, stages };
    });
    try {
      await fetch(`/api/campaigns/${campaignId}/cards/${cardId}/move`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage_id: toStageId, position: 9999 }),
      });
    } catch (err) {
      console.error('Move failed:', err);
      fetchCampaign();
    }
  };

  const removeCard = async (cardId: number) => {
    if (!window.confirm('Remove this member from the campaign? (The candidate/company itself is not deleted.)')) return;
    setCampaign((prev: any) => prev && {
      ...prev,
      stages: prev.stages.map((s: any) => ({ ...s, cards: (s.cards || []).filter((c: any) => c.id !== cardId) })),
    });
    try {
      await fetch(`/api/campaigns/${campaignId}/cards/${cardId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error('Remove failed:', err);
      fetchCampaign();
    }
  };

  const addStage = async () => {
    const name = window.prompt('New stage name');
    if (!name || !name.trim()) return;
    try {
      const resp = await fetch(`/api/campaigns/${campaignId}/stages`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      if (resp.ok) fetchCampaign();
    } catch (err) {
      console.error('Add stage failed:', err);
    }
  };

  const updateStatus = async (status: string) => {
    setCampaign((prev: any) => prev && { ...prev, status });
    try {
      await fetch(`/api/campaigns/${campaignId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
    } catch (err) {
      console.error('Status update failed:', err);
    }
  };

  const deleteCampaign = async () => {
    if (!window.confirm('Delete this campaign? Members are removed from it; the underlying candidates/companies are not deleted.')) return;
    try {
      await fetch(`/api/campaigns/${campaignId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      navigate('/campaigns');
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  }
  if (notFound || !campaign) {
    return (
      <div className="card p-12 text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Campaign not found</h3>
        <button onClick={() => navigate('/campaigns')} className="mt-2 text-blue-600 hover:underline text-sm">Back to Campaigns</button>
      </div>
    );
  }

  const statusOptions = campaign.mode === 'bd'
    ? ['active', 'paused', 'completed']
    : ['open', 'on-hold', 'filled', 'closed'];

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'todo', label: 'To do' },
    { key: 'sequence', label: 'Sequence' },
    { key: 'leads', label: 'Leads List' },
    { key: 'launch', label: 'Launch' },
    { key: 'performance', label: 'Performance' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-4">
        <div className="min-w-0">
          <button onClick={() => navigate('/campaigns')} className="text-sm text-gray-500 hover:text-gray-800 inline-flex items-center gap-1 mb-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Campaigns
          </button>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight truncate">{campaign.name}</h1>
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${MODE_BADGE[campaign.mode] || 'bg-gray-100 text-gray-600'}`}>
              {MODE_LABEL[campaign.mode] || campaign.mode}
            </span>
          </div>
          {(campaign.company_name || campaign.candidate_name || campaign.location) && (
            <p className="text-sm text-gray-500 mt-0.5 truncate">
              {campaign.mode === 'bd' ? campaign.candidate_name : [campaign.company_name, campaign.location].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select
            value={campaign.status || ''}
            onChange={e => updateStatus(e.target.value)}
            className="text-sm input bg-white"
          >
            {statusOptions.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <button onClick={deleteCampaign} className="btn-ghost text-sm text-red-600 hover:bg-red-50">Delete</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-5 border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              activeTab === tab.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {tab.label}
            {tab.key === 'leads' && <span className="ml-1.5 text-xs text-gray-400">{memberCount}</span>}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'todo' && (
        <CampaignOutreach campaignId={campaignId!} token={token} onChanged={() => fetchCampaign(true)} />
      )}

      {activeTab === 'sequence' && (
        <div className="card p-10 text-center text-gray-500">
          <h3 className="font-semibold text-gray-800 mb-1">Sequence lives in your outreach tool</h3>
          <p className="text-sm max-w-md mx-auto">Build your steps in lemlist or SourceWhale. Push members from the <strong>Launch</strong> tab; native sequences may come later.</p>
        </div>
      )}

      {activeTab === 'leads' && (
        <div>
          {/* View toggle: Table (data-table parity) | Board (pipeline) */}
          <div className="flex items-center justify-between mb-3">
            {campaign.mode !== 'bd' && campaign.project_id ? (
              <button
                onClick={openSignals}
                className="btn-secondary text-xs inline-flex items-center gap-1.5"
                title="Turn recent monitoring moves (job changes / backfills) into outreach to the company's contacts"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                Add from monitoring
              </button>
            ) : <div />}
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
              {(['table', 'board'] as const).map(v => (
                <button
                  key={v}
                  onClick={() => setLeadsView(v)}
                  className={`px-3 py-1.5 text-xs font-medium ${leadsView === v ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  {v === 'table' ? 'Table' : 'Board'}
                </button>
              ))}
            </div>
          </div>

          {leadsView === 'table' ? (
            campaign.project_id ? (
              // Leads List = the real data table, scoped to this campaign's
              // project and members (design decision: campaigns are single-project).
              <TableView
                key={`campaign-${campaignId}-${leadsReloadKey}`}
                selectedProject={{ id: campaign.project_id, name: campaign.name }}
                campaignScopeId={Number(campaignId)}
                initialDataType={campaign.mode === 'bd' ? 'companies' : 'candidates'}
              />
            ) : (
              // Legacy campaigns with no resolvable project fall back to the
              // campaign-native table.
              <CampaignLeadsList
                campaign={campaign}
                columns={columns}
                token={token}
                campaignId={campaignId!}
                onMoveCard={moveCard}
                onRemoveCard={removeCard}
                onOpenEntity={(entityType, entityId) => setPanel({ open: true, entityType: entityType as any, entityId })}
                onColumnsChanged={() => { fetchColumns(); fetchCampaign(); }}
              />
            )
          ) : (
            <PipelineBoard
              campaign={campaign}
              dragCardId={dragCardId}
              setDragCardId={setDragCardId}
              onMoveCard={moveCard}
              onRemoveCard={removeCard}
              onAddStage={addStage}
            />
          )}
        </div>
      )}

      {activeTab === 'launch' && (
        <CampaignLaunch campaignId={campaignId!} token={token} />
      )}

      {activeTab === 'performance' && (
        <CampaignPerformance campaignId={campaignId!} token={token} />
      )}

      {/* Add-from-monitoring-signals modal */}
      {showSignals && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !addingSignals && !previewLoading && setShowSignals(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between p-5 pb-3 border-b border-gray-100">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Add from monitoring signals</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Turn a candidate's move into outreach to the relevant company's contacts. The person who moved
                  becomes the subject of the message ({'{{subject_name}}'}, {'{{subject_title}}'}), not the recipient.
                </p>
              </div>
              <button onClick={() => !addingSignals && !previewLoading && setShowSignals(false)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Step 1 — direction + date range */}
            <div className="p-5 pb-3 border-b border-gray-100">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Opportunity type</label>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {([
                  { key: 'job_change', title: 'Job changes', desc: 'Reach the company they just joined — "I saw X join your team"' },
                  { key: 'backfill', title: 'Backfills', desc: 'Reach the company they just left — "I saw X leave your team"' },
                ] as const).map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => { setSignalDirection(opt.key); setPreviewRows(null); }}
                    className={`text-left rounded-lg border p-3 transition-colors ${
                      signalDirection === opt.key ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-sm font-semibold text-gray-800">{opt.title}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">{opt.desc}</div>
                  </button>
                ))}
              </div>
              <div className="flex items-end gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">From</label>
                  <input type="date" value={signalStart} onChange={e => setSignalStart(e.target.value)} className="input text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">To</label>
                  <input type="date" value={signalEnd} onChange={e => setSignalEnd(e.target.value)} className="input text-sm" />
                </div>
                <button
                  onClick={runSignalPreview}
                  disabled={previewLoading}
                  className="btn-secondary text-sm ml-auto inline-flex items-center gap-1.5"
                >
                  {previewLoading && <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-500" />}
                  {previewLoading ? 'Loading…' : 'Preview'}
                </button>
              </div>
              <p className="text-[11px] text-gray-400 mt-1.5">Leave dates blank to include the last 90 days.</p>
            </div>

            {/* Step 2 — review list */}
            <div className="flex-1 overflow-y-auto p-5 pt-3">
              {previewRows === null ? (
                <p className="text-sm text-gray-400 text-center py-6">Choose a type and date range, then Preview.</p>
              ) : previewRows.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">No {signalDirection === 'backfill' ? 'backfills' : 'job changes'} found in this window.</p>
              ) : (
                <div className="space-y-3">
                  {previewRows.map((row: any, i: number) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm text-gray-900">
                            <span className="font-semibold">{row.subjectName}</span>{' '}
                            {signalDirection === 'backfill'
                              ? <>left <span className="font-medium">{row.fromCompany}</span>{row.toCompany ? <> (now at {row.toCompany})</> : null}</>
                              : <>joined <span className="font-medium">{row.toCompany}</span>{row.fromCompany ? <> (from {row.fromCompany})</> : null}</>}
                          </p>
                          <p className="text-[11px] text-gray-500 mt-0.5">
                            Reach out to contacts at <span className="font-medium text-gray-700">{row.targetCompany}</span>
                            {(signalDirection === 'backfill' ? row.subjectOldTitle : row.subjectNewTitle)
                              ? ` · role: ${signalDirection === 'backfill' ? row.subjectOldTitle : row.subjectNewTitle}` : ''}
                          </p>
                        </div>
                      </div>

                      {row.contacts && row.contacts.length > 0 ? (
                        <div className="mt-2 space-y-1">
                          {row.contacts.map((c: any) => {
                            const key = `${i}:${c.id}`;
                            return (
                              <label key={c.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer rounded px-1.5 py-1 hover:bg-gray-50">
                                <input
                                  type="checkbox"
                                  checked={selectedContacts.has(key)}
                                  onChange={() => toggleSignalContact(key)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="font-medium">{c.name || 'Unknown'}</span>
                                {c.jobTitle && <span className="text-gray-500 truncate">· {c.jobTitle}</span>}
                                {c.email && <span className="text-[10px] text-green-600 ml-auto" title={c.email}>✉︎</span>}
                                {c.linkedin && <a href={c.linkedin} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[10px] text-blue-500 hover:underline">in</a>}
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="mt-2 flex items-center gap-2 text-[11px] text-gray-500 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
                          <span>No contacts mapped at {row.targetCompany}.</span>
                          {row.targetCompanyId ? (
                            <button
                              onClick={() => { setShowSignals(false); setPanel({ open: true, entityType: 'company', entityId: row.targetCompanyId }); }}
                              className="text-blue-600 hover:underline font-medium"
                            >
                              Find contacts →
                            </button>
                          ) : (
                            <span className="text-gray-400">Company not in this project yet.</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-2 p-5 pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-500">{selectedCount > 0 ? `${selectedCount} contact${selectedCount === 1 ? '' : 's'} selected` : ''}{signalMsg ? (selectedCount > 0 ? ' · ' : '') + signalMsg : ''}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => !addingSignals && setShowSignals(false)} className="btn-ghost text-sm">Close</button>
                <button
                  onClick={addSelectedSignalContacts}
                  disabled={addingSignals || selectedCount === 0}
                  className="btn-primary text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  {addingSignals && <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />}
                  {addingSignals ? 'Adding…' : `Add ${selectedCount || ''} to campaign`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Member side panel (reused from the data table) */}
      <EntityDetailPanel
        isOpen={panel.open}
        onClose={() => setPanel(p => ({ ...p, open: false }))}
        entityType={panel.entityType}
        entityId={panel.entityId}
        onEnrichmentComplete={fetchCampaign}
      />
    </div>
  );
};


// ─── Pipeline board (Kanban) ───────────────────────────────────────────

const PipelineBoard: React.FC<{
  campaign: any;
  dragCardId: number | null;
  setDragCardId: (id: number | null) => void;
  onMoveCard: (cardId: number, toStageId: number) => void;
  onRemoveCard: (cardId: number) => void;
  onAddStage: () => void;
}> = ({ campaign, dragCardId, setDragCardId, onMoveCard, onRemoveCard, onAddStage }) => {
  const [dragOverStage, setDragOverStage] = useState<number | null>(null);

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {(campaign.stages || []).map((stage: any) => (
        <div
          key={stage.id}
          onDragOver={e => { e.preventDefault(); setDragOverStage(stage.id); }}
          onDragLeave={() => setDragOverStage(prev => (prev === stage.id ? null : prev))}
          onDrop={e => {
            e.preventDefault();
            setDragOverStage(null);
            if (dragCardId != null) { onMoveCard(dragCardId, stage.id); setDragCardId(null); }
          }}
          className={`flex-shrink-0 w-72 rounded-xl p-3 transition-colors ${dragOverStage === stage.id ? 'bg-blue-50' : 'bg-gray-50'}`}
        >
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
              <span className="text-sm font-semibold text-gray-800">{stage.name}</span>
              <span className="text-xs text-gray-400">{(stage.cards || []).length}</span>
            </div>
          </div>

          <div className="space-y-2 min-h-[40px]">
            {(stage.cards || []).map((card: any) => (
              <div
                key={card.id}
                draggable
                onDragStart={() => setDragCardId(card.id)}
                onDragEnd={() => setDragCardId(null)}
                className="group bg-white rounded-lg border border-gray-200/80 shadow-card p-3 cursor-grab active:cursor-grabbing"
              >
                <div className="flex items-start gap-2">
                  {card.entity_photo ? (
                    <img src={card.entity_photo} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      {(card.entity_name || '?')[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-sm font-medium text-gray-900 truncate">{card.entity_name || 'Unknown'}</span>
                      <button
                        onClick={() => onRemoveCard(card.id)}
                        className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 flex-shrink-0"
                        title="Remove from campaign"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                    {card.entity_title && <p className="text-xs text-gray-500 truncate">{card.entity_title}</p>}
                    {card.entity_company && <p className="text-xs text-gray-400 truncate">{card.entity_company}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      {card.entity_email && <span className="text-[10px] text-green-600" title={card.entity_email}>✉︎</span>}
                      {card.entity_linkedin && (
                        <a href={card.entity_linkedin} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[10px] text-blue-500 hover:underline">in</a>
                      )}
                      <span className="text-[9px] uppercase tracking-wide text-gray-300">{card.entity_type}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Add stage */}
      <button
        onClick={onAddStage}
        className="flex-shrink-0 w-56 rounded-xl border-2 border-dashed border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-500 text-sm font-medium flex items-center justify-center gap-1"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
        Add stage
      </button>
    </div>
  );
};


export default CampaignWorkspace;
